import React, { useEffect } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { Loader2 } from 'lucide-react';
import OurModal from '@/components/Modal';

interface Props {
  open: boolean;
  onCancel: () => void;
  onUnlock: () => void;
  price?: string;
  originalPrice?: string;
  contentFunction: string;
  loading?: boolean;
}

const PayModal: React.FC<Props> = ({
  open,
  onCancel,
  onUnlock,
  price = '12.99',
  originalPrice = '19.99',
  contentFunction,
  loading = false,
}) => {
  const { t } = useTranslation('common');

  const features = [
    {
      emoji: '🔍',
      title: 'All Image Results',
      description: 'See every related image.',
    },
    {
      emoji: '📍',
      title: 'Website Sources',
      description: 'Know exactly where the images are from.',
    },
    {
      emoji: '🚀',
      title: 'Access Anytime',
      description: 'Your data is saved for future use.',
    },
    {
      emoji: '💰',
      title: 'One-Time Payment',
      description: 'No recurring charges, pay once.',
    },
  ];

  useEffect(() => {
    if (contentFunction === 'face') {
      ga_logEvent(EventName.trigger_face_paywall);
    } else if (contentFunction === 'photo') {
      ga_logEvent(EventName.trigger_reverse_image_paywall);
    }
  }, [contentFunction]);

  return (
    <OurModal
      isModalOpen={open}
      handleCancel={onCancel}
      width={494}
      isCenter
      closeIcon={false}
      isNoAddAccount={false}
      title={null}
      closable={false}
      zIndex={1031}
    >
      <div className={styles.payModal}>
        {/* 关闭按钮 */}
        <button className={styles.closeBtn} onClick={onCancel}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2L14 14M14 2L2 14" stroke="#0C0C0C" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* 标题 */}
        <div className={styles.title}>{t('Unlock Full Results')}</div>

        {/* 副标题 */}
        <div className={styles.subtitle}>{t("Don't miss out on key information!")}</div>

        {/* 功能卡片 */}
        <div className={styles.featuresCard}>
          {/* 卡片标题 */}
          <div className={styles.cardTitle}>{t('What you get')} 🔥</div>

          {/* 功能列表 */}
          <div className={styles.featuresList}>
            {features.map((feature, index) => (
              <div key={index} className={styles.featureItem}>
                <span className={styles.emoji}>{feature.emoji}</span>
                <div className={styles.featureContent}>
                  <div className={styles.featureTitle}>{t(feature.title)}</div>
                  <div className={styles.featureDesc}>- {t(feature.description)}</div>
                </div>
              </div>
            ))}
          </div>

          {/* 分隔线 */}
          <div className={styles.divider} />

          {/* 底部价格和按钮 */}
          <div className={styles.footer}>
            <div className={styles.priceWrapper}>
              <span className={styles.currentPrice}>
                ${price.split('.')[0]}
                <span className={styles.priceDecimal}>.{price.split('.')[1]}</span>
              </span>
              {originalPrice && <span className={styles.originalPrice}>${originalPrice}</span>}
            </div>
            <button
              className={`${styles.unlockBtn} ${loading ? styles.loading : ''}`}
              onClick={onUnlock}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className={styles.spinner} size={18} />
                  {t('Loading...')}
                </>
              ) : (
                `🚀 ${t('Unlock Now')}`
              )}
            </button>
          </div>
        </div>
      </div>
    </OurModal>
  );
};

export default PayModal;
